import { baseUrlRes1 } from '../../constants/baseUrl';
import axiosInstance from './axiosConfig';
import cookieHelper from './cookieHelper';

/**
 * Gets the authentication token from Cookies or localStorage
 * @returns {string} The authentication token or empty string if not found
 */
export const getAuthToken = () => {
  return cookieHelper.getToken() || localStorage.getItem('id_token') || '';
};

// Map to track pending in-flight GET requests and deduplicate concurrent identical calls
const inFlightGetRequests = new Map();

export const clearInFlightRequests = () => {
  inFlightGetRequests.clear();
};

if (typeof window !== 'undefined') {
  window.addEventListener('userLoggedOut', clearInFlightRequests);
  window.addEventListener('authStateChanged', clearInFlightRequests);
}

const buildRequestKey = (endpoint, params) => {
  if (!params || typeof params !== 'object' || Object.keys(params).length === 0) {
    return endpoint;
  }
  try {
    const sortedEntries = Object.entries(params)
      .filter(([_, v]) => v !== undefined && v !== null)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(typeof v === 'object' ? JSON.stringify(v) : String(v))}`);
    return sortedEntries.length > 0 ? `${endpoint}?${sortedEntries.join('&')}` : endpoint;
  } catch (e) {
    return `${endpoint}_${JSON.stringify(params)}`;
  }
};

/**
 * Standardized GET request with in-flight deduplication
 * @param {string} endpoint - API endpoint to call
 * @param {Object} params - Query parameters (optional)
 * @returns {Promise} Promise that resolves to the API response
 */
export const apiGet = async (endpoint, params = {}, cache = true) => {
  const reqKey = buildRequestKey(endpoint, params);

  // If an identical GET request is already in-flight, return the existing promise
  if (inFlightGetRequests.has(reqKey)) {
    return inFlightGetRequests.get(reqKey);
  }

  let requestPromise;
  requestPromise = (async () => {
    try {
      const response = await axiosInstance.get(endpoint, {
        params,
        cache: cache ? undefined : false // false will bypass axios-cache-interceptor
      });
      return response.data;
    } catch (error) {
      console.error(`Error in apiGet call to ${endpoint}:`, error);
      const data = error.response?.data;
      return {
        status: "Error",
        message:
          data?.message ||
          data?.error ||
          error.message ||
          "An error occurred with the request",
      };
    } finally {
      if (inFlightGetRequests.get(reqKey) === requestPromise) {
        inFlightGetRequests.delete(reqKey);
      }
    }
  })();

  inFlightGetRequests.set(reqKey, requestPromise);
  return requestPromise;
};

/**
 * Standardized POST request
 * @param {string} endpoint - API endpoint to call
 * @param {Object|FormData} data - Request body
 * @param {Object} customHeaders - Optional custom headers
 * @returns {Promise} Promise that resolves to the API response
 */
export const apiPost = async (endpoint, data, customHeaders = null) => {
  try {
    const config = customHeaders ? { headers: customHeaders } : {};
    const response = await axiosInstance.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    console.error(`Error in apiPost call to ${endpoint}:`, error);
    return {
      status: "Error",
      message: error.response?.data?.error || error.response?.data?.message || error.message || "An error occurred with the request",
      error: error.response?.data?.error
    };
  }
};

/**
 * Standardized PUT request
 * @param {string} endpoint - API endpoint to call
 * @param {Object} data - Request body
 * @returns {Promise} Promise that resolves to the API response
 */
export const apiPut = async (endpoint, data) => {
  try {
    const response = await axiosInstance.put(endpoint, data);
    return response.data;
  } catch (error) {
    console.error(`Error in apiPut call to ${endpoint}:`, error);
    return {
      status: "Error",
      message: error.response?.data?.message || error.message || "An error occurred with the request"
    };
  }
};

/**
 * Standardized PATCH request
 * @param {string} endpoint - API endpoint to call
 * @param {Object} data - Request body
 * @returns {Promise} Promise that resolves to the API response
 */
export const apiPatch = async (endpoint, data) => {
  try {
    const response = await axiosInstance.patch(endpoint, data);
    return response.data;
  } catch (error) {
    console.error(`Error in apiPatch call to ${endpoint}:`, error);
    return {
      status: "Error",
      message: error.response?.data?.message || error.message || "An error occurred with the request"
    };
  }
};

/**
 * Standardized DELETE request
 * @param {string} endpoint - API endpoint to call
 * @param {Object} data - Request body (optional)
 * @returns {Promise} Promise that resolves to the API response
 */
export const apiDelete = async (endpoint, data = null) => {
  try {
    const response = await axiosInstance.delete(endpoint, { data });
    return response.data;
  } catch (error) {
    console.error(`Error in apiDelete call to ${endpoint}:`, error);
    return {
      status: "Error",
      message: error.response?.data?.message || error.message || "An error occurred with the request"
    };
  }
};
